import { useState } from "react";
import {
  buildFromTemplate,
  createGameCard,
  GAME_TEMPLATES,
  type GameCard,
  type GameTemplate,
  type GameTemplateField,
} from "@roqeet/core";
import { Compass, MessageCircle, Dices, VenetianMask, Key, Vote, Gamepad2, Sparkles, Store, Map, ShieldCheck } from "lucide-react";
import { saveGameCard } from "../../state/game-cards.js";
import { useT } from "../../i18n/use-t.js";
import { useEscapeToClose } from "../use-escape-to-close.js";

/**
 * 게임 템플릿 위저드 — 결정 #35(텍스트 프리셋, 제작 사다리의 최저 진입장벽). 장르
 * 템플릿을 고르고 빈칸 몇 개만 채우면 완성된 게임이 저장된다. 폼 에디터(#29)보다
 * 더 쉬운 진입점으로, 대다수 사용자의 첫 제작 경험을 노린다.
 *
 * 흐름: 템플릿 선택 → 빈칸 입력 → 만들기(저장·등록). 만든 카드는 같은 포맷이라
 * 이후 폼 에디터에서 더 다듬을 수 있다(상향 호환, 결정 #35). 만든 직후 편집기로
 * 이어가도록 onCreated(card)를 호출해 호출부가 연결한다.
 */
export function GameTemplateWizard({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated?: (card: GameCard) => void;
}) {
  const { t } = useT();
  const [selected, setSelected] = useState<GameTemplate | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEscapeToClose(onClose, !busy);

  const pick = (tpl: GameTemplate) => {
    setSelected(tpl);
    // 기본값으로 미리 채워 빈칸을 안 채워도 바로 만들 수 있게 한다(결정 #35).
    const seeded: Record<string, string> = {};
    for (const f of tpl.fields) seeded[f.key] = templateFieldDefault(t, tpl.id, f) ?? f.default ?? "";
    setValues(seeded);
    setError(null);
  };

  const create = async (openEditor: boolean) => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const definition = buildFromTemplate(selected, values, (k, p) => t(k as any, p));
      const sourceRqt = selected.source?.(values);
      const card = createGameCard({
        definition,
        enabled: true,
        ...(sourceRqt ? { extensions: { sourceRqt, sourceRqtStatus: "current" } } : {}),
      });
      await saveGameCard(card);
      onClose();
      if (openEditor) onCreated?.(card);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex max-h-screen max-h-[100dvh] w-full max-w-md flex-col overflow-hidden bg-bg sm:max-h-[calc(100vh-2rem)] sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl sm:border sm:border-line">
        {/* 헤더 */}
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-line px-4">
          <div className="flex items-center gap-2">
            {selected && (
              <button
                className="rounded p-1 text-ink-faint hover:bg-bg-raised hover:text-ink"
                onClick={() => setSelected(null)}
                aria-label={t("gameWiz.back")}
              >
                ‹
              </button>
            )}
            <h2 className="text-sm font-semibold text-ink">
              {selected ? templateTitle(t, selected.id) : t("gameWiz.title")}
            </h2>
          </div>
          <button
            className="rounded p-1 text-ink-faint hover:bg-bg-raised hover:text-ink"
            onClick={onClose}
            aria-label={t("misc.close")}
          >
            ✕
          </button>
        </div>

        {/* 1단계: 템플릿 선택 */}
        {!selected && (
          <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3 scroll-touch">
            <li className="px-1 pb-1 text-xs text-ink-faint">
              {t("gameWiz.intro")}
            </li>
            {GAME_TEMPLATES.map((tpl) => (
              <li key={tpl.id}>
                <button
                  className="flex w-full items-start gap-3 rounded-xl border border-line bg-bg-soft px-3 py-3 text-left hover:border-accent hover:bg-bg-raised"
                  onClick={() => pick(tpl)}
                >
                  <span className="mt-0.5" aria-hidden>
                    {iconFor(tpl.icon)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-ink">{templateTitle(t, tpl.id)}</span>
                    <span className="mt-0.5 block text-xs text-ink-faint">{templateDescription(t, tpl.id)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* 2단계: 빈칸 채우기 */}
        {selected && (
          <>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 scroll-touch">
              <p className="text-xs text-ink-faint">{templateDescription(t, selected.id)}</p>
              {selected.fields.map((field) => (
                <label key={field.key} className="block">
                  <span className="mb-1 block text-sm font-medium text-ink">
                    {templateFieldLabel(t, selected.id, field)}
                  </span>
                  {field.multiline ? (
                    <textarea
                      className="min-h-[60px] w-full resize-y rounded-lg border border-line bg-bg-soft px-3 py-2 text-sm text-ink outline-none focus:border-accent"
                      value={values[field.key] ?? ""}
                      placeholder={templateFieldPlaceholder(t, selected.id, field) ?? field.placeholder}
                      onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                    />
                  ) : (
                    <input
                      className="w-full rounded-lg border border-line bg-bg-soft px-3 py-2 text-sm text-ink outline-none focus:border-accent"
                      value={values[field.key] ?? ""}
                      placeholder={templateFieldPlaceholder(t, selected.id, field) ?? field.placeholder}
                      onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                    />
                  )}
                </label>
              ))}
              {error && (
                <div className="rounded-lg border border-red-800 bg-red-950/40 px-3 py-2 text-xs text-red-300">
                  {error}
                </div>
              )}
              <p className="text-[11px] text-ink-faint">
                {t("gameWiz.afterCreateHint")}
              </p>
            </div>

            {/* 푸터 */}
            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-line p-3">
              <button
                className="rounded-lg px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised disabled:opacity-50"
                onClick={() => create(true)}
                disabled={busy}
              >
                {t("gameWiz.createAndEdit")}
              </button>
              <button
                className="rounded-lg bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
                onClick={() => create(false)}
                disabled={busy}
              >
                {t("gameWiz.create")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

type Translate = ReturnType<typeof useT>["t"];

function templateTitle(t: Translate, id: string): string {
  switch (id) {
    case "tpl-escape-room":
      return t("gameTpl.escape.title");
    case "tpl-dungeon":
      return t("gameTpl.dungeon.title");
    case "tpl-dating":
      return t("gameTpl.dating.title");
    case "tpl-mystery":
      return t("gameTpl.mystery.title");
    default:
      return GAME_TEMPLATES.find((tpl) => tpl.id === id)?.title ?? id;
  }
}

function templateDescription(t: Translate, id: string): string {
  switch (id) {
    case "tpl-escape-room":
      return t("gameTpl.escape.desc");
    case "tpl-dungeon":
      return t("gameTpl.dungeon.desc");
    case "tpl-dating":
      return t("gameTpl.dating.desc");
    case "tpl-mystery":
      return t("gameTpl.mystery.desc");
    default:
      return GAME_TEMPLATES.find((tpl) => tpl.id === id)?.description ?? "";
  }
}

function templateFieldLabel(t: Translate, tplId: string, field: GameTemplateField): string {
  const text = templateFieldText(t, tplId, field.key, "label");
  return text ?? field.label ?? field.key;
}

function templateFieldPlaceholder(t: Translate, tplId: string, field: GameTemplateField): string | undefined {
  return templateFieldText(t, tplId, field.key, "placeholder") ?? field.placeholder;
}

function templateFieldDefault(t: Translate, tplId: string, field: GameTemplateField): string | undefined {
  return templateFieldText(t, tplId, field.key, "default") ?? field.default;
}

function templateFieldText(
  t: Translate,
  tplId: string,
  key: string,
  part: "label" | "placeholder" | "default",
): string | undefined {
  const map: Record<string, Record<string, Partial<Record<"label" | "placeholder" | "default", Parameters<Translate>[0]>>>> = {
    "tpl-escape-room": {
      theme: {
        label: "gameTpl.escape.theme.label",
        placeholder: "gameTpl.escape.theme.placeholder",
        default: "gameTpl.escape.theme.default",
      },
      goal: {
        label: "gameTpl.escape.goal.label",
        placeholder: "gameTpl.escape.goal.placeholder",
        default: "gameTpl.escape.goal.default",
      },
      clues: {
        label: "gameTpl.escape.clues.label",
        placeholder: "gameTpl.number.placeholder",
        default: "gameTpl.escape.clues.default",
      },
    },
    "tpl-dungeon": {
      setting: {
        label: "gameTpl.dungeon.setting.label",
        placeholder: "gameTpl.dungeon.setting.placeholder",
        default: "gameTpl.dungeon.setting.default",
      },
      boss: {
        label: "gameTpl.dungeon.boss.label",
        placeholder: "gameTpl.dungeon.boss.placeholder",
        default: "gameTpl.dungeon.boss.default",
      },
      hp: {
        label: "gameTpl.dungeon.hp.label",
        placeholder: "gameTpl.number.placeholder",
        default: "gameTpl.dungeon.hp.default",
      },
    },
    "tpl-dating": {
      setting: {
        label: "gameTpl.dating.setting.label",
        placeholder: "gameTpl.dating.setting.placeholder",
        default: "gameTpl.dating.setting.default",
      },
      name: {
        label: "gameTpl.dating.name.label",
        placeholder: "gameTpl.dating.name.placeholder",
        default: "gameTpl.dating.name.default",
      },
      personality: {
        label: "gameTpl.dating.personality.label",
        placeholder: "gameTpl.dating.personality.placeholder",
        default: "gameTpl.dating.personality.default",
      },
    },
    "tpl-mystery": {
      setting: {
        label: "gameTpl.mystery.setting.label",
        placeholder: "gameTpl.mystery.setting.placeholder",
        default: "gameTpl.mystery.setting.default",
      },
      crime: {
        label: "gameTpl.mystery.crime.label",
        placeholder: "gameTpl.mystery.crime.placeholder",
        default: "gameTpl.mystery.crime.default",
      },
      suspects: {
        label: "gameTpl.mystery.suspects.label",
        placeholder: "gameTpl.mystery.suspects.placeholder",
        default: "gameTpl.mystery.suspects.default",
      },
    },
  };
  const keyName = map[tplId]?.[key]?.[part];
  return keyName ? t(keyName) : undefined;
}

/** 아이콘 힌트를 SVG 아이콘으로 */
function iconFor(icon: string | undefined, className = "w-5 h-5 text-accent"): React.ReactNode {
  switch (icon) {
    case "compass": return <Compass className={className} />;
    case "chat": return <MessageCircle className={className} />;
    case "dice": return <Dices className={className} />;
    case "mask": return <VenetianMask className={className} />;
    case "key": return <Key className={className} />;
    case "vote": return <Vote className={className} />;
    case "sparkles": return <Sparkles className={className} />;
    case "store": return <Store className={className} />;
    case "map": return <Map className={className} />;
    case "shield": return <ShieldCheck className={className} />;
    default: return <Gamepad2 className={className} />;
  }
}
